import React, { useMemo, useState } from "react"
import { useTranslation } from 'react-i18next'
import { Modal } from 'react-bootstrap'
import ReactApexChart from 'react-apexcharts'

import { httpOk, GET } from 'helpers/api'
import { formatMoney, formatDate } from 'helpers/helpers'
import { FilterSegment } from 'pages/main/analytics/parts/AnalyticsFilterBar'
import useAnalyticsOptions from 'pages/main/analytics/parts/useAnalyticsOptions'
import { buildAnalyticsUrl } from 'pages/main/analytics/parts/analyticsDates'
import ChainFilterBar, { useChainFilters, FilterField, FilterSelect, ExcelButton } from '../parts/ChainFilterBar'
import { downloadExcel, periodText } from '../parts/chainExcel'
import {
	money, percent, columnStats, heatStyle, intensityStyle, buildRanks, chainColor,
	RankBadge, Delta, FillCell, useSort, SortTh, ChainKpi, HeatLegend, ChainEmpty, TableSearch,
} from '../parts/chainUi'

// Количество: целое — без дробей, вес — до граммов
function qty(value) {
	const number = Number(value || 0)
	if (Number.isInteger(number)) return formatMoney(number)
	return formatMoney(number, 3).replace(/\.?0+$/, '')
}

// Дни → месяцы, когда период длинный: 365 столбиков не читаются
function groupSeries(daily, byMonth) {
	if (!byMonth) {
		return { labels: daily.days.map(day => day.slice(8, 10) + '.' + day.slice(5, 7)), series: daily.series }
	}
	const months = [...new Set(daily.days.map(day => day.slice(0, 7)))]
	return {
		labels: months.map(month => month.slice(5, 7) + '.' + month.slice(0, 4)),
		series: daily.series.map(item => ({
			...item,
			data: months.map(month => daily.days.reduce((sum, day, index) => day.startsWith(month) ? sum + Number(item.data[index] || 0) : sum, 0)),
		})),
	}
}

function WriteOffs() {
	const { t } = useTranslation()
	const [data, setData] = useState(null)
	const [cellMode, setCellMode] = useState('amount')
	const [search, setSearch] = useState('')
	const [details, setDetails] = useState(null)
	const [openedDoc, setOpenedDoc] = useState(null)

	const filters = useChainFilters({ wms_ids: [], category_ids: [], product_type_ids: [] }, searchWithFilter)
	const { draft, patch, applied } = filters
	const options = useAnalyticsOptions(['categories', 'warehouses'])

	const typeLabels = useMemo(() => ({
		0: t('element'),
		1: t('product'),
		2: t('dish_preparation'),
		3: t('modifiers'),
	}), [t])
	const typeOptions = useMemo(() => [0, 1, 2, 3].map(id => ({ id, name: typeLabels[id] })), [typeLabels])
	const categoryOptions = useMemo(() => [{ id: 0, name: t('uncategorized') }, ...options.categories], [options.categories, t])

	// Склады выбранных филиалов, подписаны филиалом: у разных точек склады называются одинаково
	const warehouseOptions = useMemo(() => {
		const posNames = Object.fromEntries(options.poses.map(pos => [pos.id, pos.short_name || pos.name]))
		const selected = draft.pos_ids || []
		return options.warehouses
			.filter(item => selected.length === 0 || selected.includes(item.pos_id))
			.map(item => ({ id: item.id, name: posNames[item.pos_id] ? `${item.name} · ${posNames[item.pos_id]}` : item.name }))
	}, [options.warehouses, options.poses, draft.pos_ids])

	async function searchWithFilter(filter) {
		const response = await GET(buildAnalyticsUrl('/chain/report/write-offs', filter), {}, { loader: false })
		if (httpOk(response)) setData(response.data)
	}

	const branches = useMemo(() => data?.branches || [], [data])
	const totals = data?.totals || {}
	const branchById = useMemo(() => Object.fromEntries(branches.map(item => [item.pos_id, item])), [branches])
	// В матрице — только филиалы, где что-то списывали
	const matrixBranches = useMemo(() => {
		const active = branches.filter(item => item.amount > 0)
		return active.length > 0 ? active : branches
	}, [branches])

	// Рейтинг: меньше списаний от выручки — выше место
	const { sorted: sortedBranches, sort: branchSort, toggle: toggleBranchSort } = useSort(branches, 'amount')
	const { ranks, count } = useMemo(() => buildRanks(branches, 'revenue_percent', false), [branches])
	const stats = useMemo(() => ({
		revenue_percent: columnStats(branches, 'revenue_percent'),
		amount_delta: columnStats(branches, 'amount_delta'),
		avg_document: columnStats(branches, 'avg_document'),
	}), [branches])
	const maxShare = useMemo(() => columnStats(branches, 'share').max, [branches])

	const products = useMemo(() => {
		const list = data?.products || []
		const query = search.trim().toLowerCase()
		if (!query) return list
		return list.filter(item => `${item.product_name} ${item.category_name || ''}`.toLowerCase().includes(query))
	}, [data, search])

	function cellValue(product, posId) {
		const source = cellMode === 'amount' ? product.poses : product.quantities
		return Number(source?.[posId] || 0)
	}

	const readSortValue = (row, key) => key.startsWith('pos:') ? cellValue(row, Number(key.slice(4))) : row[key]
	const { sorted: sortedProducts, sort: productSort, toggle: toggleProductSort } = useSort(products, 'amount', 'desc', readSortValue)

	const reasons = data?.reasons || []
	const topBranch = useMemo(() => {
		const withAmount = branches.filter(item => item.amount > 0 && item.revenue_percent !== null)
		return withAmount.length > 0 ? [...withAmount].sort((a, b) => b.revenue_percent - a.revenue_percent)[0] : null
	}, [branches])
	const topProduct = (data?.products || [])[0]

	function reasonLabel(reason) {
		if (reason.is_other) return `${t('chain_wo_other_notes')} (${formatMoney(reason.notes)})`
		if (!reason.key) return t('chain_wo_no_note')
		return reason.note
	}

	async function openDetails({ posId = null, product = null, reason = null }) {
		const extra = { pos_id: posId, product_id: product ? product.product_id : null }
		if (reason) {
			if (reason.key) extra.note_key = reason.key
			else extra.no_note = 1
		}
		const response = await GET(buildAnalyticsUrl('/chain/report/write-offs-details', applied, extra), {}, { loader: true })
		if (httpOk(response)) {
			const parts = [posId ? branchById[posId]?.pos_name : t('chain_all_branches')]
			if (product) parts.unshift(product.product_name)
			if (reason) parts.unshift(reasonLabel(reason))
			setOpenedDoc(null)
			setDetails({ ...response.data, title: parts.join(' — '), product })
		}
	}

	async function openDocument(id) {
		const response = await GET(`/chain/report/write-offs-document?id=${id}`, {}, { loader: true })
		if (httpOk(response)) setOpenedDoc(response.data)
	}

	function closeModal() {
		setDetails(null)
		setOpenedDoc(null)
	}

	function documentTitle(row) {
		return row.document_number ? `№ ${row.document_number}` : `#${row.id}`
	}

	function exportExcel() {
		if (!data) return
		const period = `${t('period')}: ${periodText(data.start_date, data.end_date)}`
		const productRows = data.products || []
		const productColumns = (valueType) => [
			{ label: t('chain_wo_position'), type: 'text', width: 34 },
			{ label: t('unit'), type: 'text', width: 8 },
			{ label: t('category'), type: 'text', width: 18 },
			{ label: t('type'), type: 'text', width: 12 },
			...branches.map(branch => ({ label: branch.pos_name, type: valueType })),
			{ label: t('total'), type: valueType },
		]
		const productInfo = (product) => [product.product_name, product.unit_name || '', product.category_name || '', typeLabels[product.product_type_id] || '']
		const ranksByPercent = buildRanks(branches, 'revenue_percent', false).ranks
		const daily = data.daily || { days: [], series: [] }

		downloadExcel(`${t('chain_wo_title')} ${periodText(data.start_date, data.end_date)}`, [
			{
				name: t('chain_branches'),
				title: t('chain_wo_title'),
				subtitle: period,
				columns: [
					{ label: t('chain_rank'), type: 'int', width: 8 },
					{ label: t('chain_branch'), type: 'text', width: 30 },
					{ label: t('chain_write_offs'), type: 'money' },
					{ label: t('share'), type: 'percent', width: 9 },
					{ label: t('chain_prev_period'), type: 'money' },
					{ label: t('chain_growth'), type: 'percent', width: 9 },
					{ label: t('revenue'), type: 'money' },
					{ label: t('chain_percent_of_revenue'), type: 'percent', width: 14 },
					{ label: t('chain_wo_documents'), type: 'int', width: 10 },
					{ label: t('chain_wo_avg_document'), type: 'money' },
					{ label: t('chain_wo_positions'), type: 'int', width: 10 },
				],
				rows: [...branches].sort((a, b) => b.amount - a.amount).map(branch => [
					ranksByPercent[branch.pos_id], branch.pos_name, branch.amount, branch.share, branch.prev_amount, branch.amount_delta,
					branch.revenue, branch.revenue_percent, branch.documents, branch.avg_document, branch.products,
				]),
				footer: ['', t('total'), totals.amount, 100, totals.prev_amount, totals.amount_delta, totals.revenue,
					totals.revenue_percent, totals.documents, totals.avg_document, totals.products],
			},
			{
				name: `${t('chain_wo_positions')} — ${t('amount')}`,
				title: `${t('chain_wo_matrix')} — ${t('amount')}`,
				subtitle: period,
				columns: [...productColumns('money'), { label: t('share'), type: 'percent', width: 9 }],
				rows: productRows.map(product => [
					...productInfo(product),
					...branches.map(branch => Number(product.poses?.[branch.pos_id] || 0)),
					product.amount,
					product.share,
				]),
				footer: [t('total'), '', '', '', ...branches.map(branch => branch.amount), totals.amount, 100],
			},
			{
				name: `${t('chain_wo_positions')} — ${t('quantity')}`,
				title: `${t('chain_wo_matrix')} — ${t('quantity')}`,
				subtitle: period,
				columns: productColumns('number'),
				rows: productRows.map(product => [
					...productInfo(product),
					...branches.map(branch => Number(product.quantities?.[branch.pos_id] || 0)),
					product.quantity,
				]),
			},
			{
				name: t('chain_wo_reasons'),
				title: t('chain_wo_reasons'),
				subtitle: period,
				columns: [
					{ label: t('note'), type: 'text', width: 40 },
					...branches.map(branch => ({ label: branch.pos_name, type: 'money' })),
					{ label: t('total'), type: 'money' },
					{ label: t('chain_wo_documents'), type: 'int', width: 10 },
					{ label: t('share'), type: 'percent', width: 9 },
				],
				rows: reasons.map(reason => [
					reasonLabel(reason),
					...branches.map(branch => Number(reason.poses?.[branch.pos_id] || 0)),
					reason.amount, reason.documents, reason.share,
				]),
			},
			{
				name: t('chain_by_days'),
				title: `${t('chain_by_days')} — ${t('chain_write_offs')}`,
				subtitle: period,
				columns: [
					{ label: t('date'), type: 'text', width: 12 },
					...daily.series.map(series => ({ label: series.pos_name, type: 'money' })),
					{ label: t('total'), type: 'money' },
				],
				rows: daily.days.map((day, index) => {
					const values = daily.series.map(series => series.data[index])
					return [day.split('-').reverse().join('.'), ...values, values.reduce((sum, value) => sum + Number(value || 0), 0)]
				}),
			},
		])
	}

	function exportDetails() {
		if (openedDoc) {
			const doc = openedDoc.document
			downloadExcel(`${t('chain_wo_document')} ${documentTitle(doc)}`, [{
				name: t('chain_wo_document'),
				title: `${t('chain_wo_document')} ${documentTitle(doc)} — ${doc.pos_name}`,
				subtitle: `${formatDate(doc.created_at, 'dd.MM.yyyy HH:mm')} · ${doc.wms_name || ''} · ${doc.note || ''}`,
				columns: [
					{ label: t('chain_wo_position'), type: 'text', width: 34 },
					{ label: t('type'), type: 'text', width: 12 },
					{ label: t('category'), type: 'text', width: 18 },
					{ label: t('quantity'), type: 'number', width: 10 },
					{ label: t('unit'), type: 'text', width: 8 },
					{ label: t('cost_price'), type: 'money' },
					{ label: t('amount'), type: 'money' },
				],
				rows: openedDoc.lines.map(line => [
					line.product_name, typeLabels[line.product_type_id] || '', line.category_name || '',
					line.quantity, line.unit_name || '', line.cost_price, line.total_cost_price,
				]),
				footer: [t('total'), '', '', '', '', '', doc.amount],
			}])
			return
		}
		if (!details) return
		const withProduct = !!details.product
		downloadExcel(`${t('chain_wo_title')} — ${details.title}`, [{
			name: t('chain_wo_documents'),
			title: details.title,
			subtitle: `${t('period')}: ${periodText(applied.start_date, applied.end_date)}`,
			columns: [
				{ label: t('date'), type: 'text', width: 18 },
				{ label: t('number'), type: 'text', width: 10 },
				{ label: t('chain_branch'), type: 'text', width: 24 },
				{ label: t('wms'), type: 'text', width: 20 },
				{ label: t('note'), type: 'text', width: 30 },
				{ label: t('created_by'), type: 'text', width: 20 },
				{ label: t('chain_wo_positions'), type: 'int', width: 10 },
				...(withProduct ? [{ label: t('quantity'), type: 'number', width: 10 }, { label: details.product.product_name, type: 'money' }] : []),
				{ label: t('chain_wo_document_amount'), type: 'money' },
			],
			rows: details.rows.map(row => [
				formatDate(row.created_at, 'dd.MM.yyyy HH:mm'), documentTitle(row), row.pos_name, row.wms_name || '', row.note || '',
				row.created_by_name || row.created_by || '', row.lines,
				...(withProduct ? [row.product_quantity, row.product_amount] : []),
				row.amount,
			]),
			footer: [t('total'), '', '', '', '', '', '', ...(withProduct ? [details.product_quantity, details.product_total] : []), details.total],
		}])
	}

	const productRowMax = (product) => Math.max(0, ...matrixBranches.map(branch => cellValue(product, branch.pos_id)))
	const formatCell = (value) => value > 0 ? (cellMode === 'amount' ? money(value) : qty(value)) : '—'

	return (
		<>
			<ChainFilterBar
				title={t('chain_wo_title')}
				icon="uil-trash-alt"
				filters={filters}
				wide
				onExcel={exportExcel}
				excelDisabled={!data}
			>
				<FilterField label={t('warehouses')}>
					<FilterSelect isMulti options={warehouseOptions} allLabel={t('chain_all_warehouses')} value={draft.wms_ids} onChange={(ids) => patch({ wms_ids: ids })} />
				</FilterField>
				<FilterField label={t('categories')}>
					<FilterSelect isMulti options={categoryOptions} allLabel={t('chain_all_categories')} value={draft.category_ids} onChange={(ids) => patch({ category_ids: ids })} />
				</FilterField>
				<FilterField label={t('chain_wo_position_type')}>
					<FilterSelect isMulti options={typeOptions} allLabel={t('chain_all_types')} value={draft.product_type_ids} onChange={(ids) => patch({ product_type_ids: ids })} />
				</FilterField>
			</ChainFilterBar>

			{data?.filtered_lines && (
				<div className="chain-note mb-3">
					<i className="uil uil-info-circle"></i>
					<span>{t('chain_wo_filtered_note')}</span>
				</div>
			)}

			<div className="row g-3 mb-3">
				<div className="col-xl-3 col-md-6">
					<ChainKpi title={t('chain_wo_total')} icon="uil-trash-alt" color="#f06548"
						value={money(totals.amount)}
						sub={<><Delta value={totals.amount_delta} goodHigh={false} /> {t('chain_vs_prev')}</>} />
				</div>
				<div className="col-xl-3 col-md-6">
					<ChainKpi title={t('chain_percent_of_revenue')} icon="uil-percentage" color="#f59f00"
						title2={t('chain_wo_percent_hint')}
						value={percent(totals.revenue_percent, 2)}
						sub={<>{t('revenue')} {money(totals.revenue)}</>} />
				</div>
				<div className="col-xl-3 col-md-6">
					<ChainKpi title={t('chain_wo_documents')} icon="uil-file-alt" color="#5b73e8"
						value={formatMoney(totals.documents)}
						sub={<>{t('chain_wo_avg_document')} {money(totals.avg_document)} · {formatMoney(totals.products)} {t('chain_wo_positions_lower')}</>} />
				</div>
				<div className="col-xl-3 col-md-6">
					<ChainKpi title={t('chain_wo_top_branch')} icon="uil-fire" color="#f06548"
						title2={t('chain_wo_top_branch_hint')}
						value={topBranch ? topBranch.pos_name : '—'}
						sub={topBranch ? <>{percent(topBranch.revenue_percent, 2)} {t('chain_of_revenue')} · {money(topBranch.amount)}</> : null} />
				</div>
			</div>

			<div className="card chain-card mb-3">
				<div className="card-body">
					<div className="chain-toolbar">
						<div className="chain-toolbar-group">
							<h5 className="mb-0 me-2">{t('chain_rating')}</h5>
							<span className="chain-muted">{t('chain_wo_rank_hint')}</span>
						</div>
						<HeatLegend />
					</div>

					<div className="chain-scroll chain-scroll-auto">
						<table className="chain-table">
							<thead>
								<tr>
									<th className="chain-index chain-sticky">#</th>
									<SortTh sortKey="pos_name" sort={branchSort} onSort={toggleBranchSort} className="chain-sticky-2">{t('chain_branch')}</SortTh>
									<SortTh sortKey="amount" sort={branchSort} onSort={toggleBranchSort} className="text-end">{t('chain_write_offs')}</SortTh>
									<SortTh sortKey="amount_delta" sort={branchSort} onSort={toggleBranchSort} className="text-end" title={t('chain_vs_prev')}>{t('chain_growth')}</SortTh>
									<SortTh sortKey="revenue_percent" sort={branchSort} onSort={toggleBranchSort} className="text-end" title={t('chain_wo_percent_hint')}>{t('chain_percent_of_revenue')}</SortTh>
									<SortTh sortKey="revenue" sort={branchSort} onSort={toggleBranchSort} className="text-end">{t('revenue')}</SortTh>
									<SortTh sortKey="documents" sort={branchSort} onSort={toggleBranchSort} className="text-end">{t('chain_wo_documents')}</SortTh>
									<SortTh sortKey="avg_document" sort={branchSort} onSort={toggleBranchSort} className="text-end">{t('chain_wo_avg_document')}</SortTh>
									<SortTh sortKey="products" sort={branchSort} onSort={toggleBranchSort} className="text-end">{t('chain_wo_positions')}</SortTh>
								</tr>
							</thead>
							<tbody>
								{sortedBranches.length === 0 && <ChainEmpty colSpan={9} />}
								{sortedBranches.map(item => {
									const index = branches.indexOf(item)
									return (
										<tr key={item.pos_id}>
											<td className="chain-index chain-sticky"><RankBadge rank={ranks[item.pos_id]} count={count} /></td>
											<td className="chain-name chain-sticky-2">
												<span className="d-inline-flex align-items-center">
													<span className="chain-dot" style={{ background: chainColor(index) }}></span>
													{item.pos_name}
												</span>
											</td>
											<FillCell percent={maxShare > 0 ? item.share / maxShare * 100 : 0} tone="danger"
												title={item.amount > 0 ? t('chain_open_details') : undefined}
												onClick={item.amount > 0 ? () => openDetails({ posId: item.pos_id }) : undefined}>
												<b>{money(item.amount)}</b>
												<div className="chain-muted">{percent(item.share)}</div>
											</FillCell>
											<td className="chain-num">
												<Delta value={item.amount_delta} goodHigh={false} showNone={item.amount > 0} />
												<div className="chain-muted">{money(item.prev_amount)}</div>
											</td>
											<td className="chain-num" style={heatStyle(item.revenue_percent, stats.revenue_percent, false)}>
												{percent(item.revenue_percent, 2)}
											</td>
											<td className="chain-num">{money(item.revenue)}</td>
											<td className="chain-num">{formatMoney(item.documents)}</td>
											<td className="chain-num" style={heatStyle(item.avg_document, stats.avg_document, false)}>{money(item.avg_document)}</td>
											<td className="chain-num">{formatMoney(item.products)}</td>
										</tr>
									)
								})}
							</tbody>
							{sortedBranches.length > 0 && (
								<tfoot>
									<tr>
										<td className="chain-sticky"></td>
										<td className="chain-sticky-2">{t('total')}</td>
										<td className="chain-num chain-click" onClick={() => totals.amount > 0 && openDetails({})}>{money(totals.amount)}</td>
										<td className="chain-num"><Delta value={totals.amount_delta} goodHigh={false} showNone={totals.amount > 0} /></td>
										<td className="chain-num">{percent(totals.revenue_percent, 2)}</td>
										<td className="chain-num">{money(totals.revenue)}</td>
										<td className="chain-num">{formatMoney(totals.documents)}</td>
										<td className="chain-num">{money(totals.avg_document)}</td>
										<td className="chain-num">{formatMoney(totals.products)}</td>
									</tr>
								</tfoot>
							)}
						</table>
					</div>
				</div>
			</div>

			<div className="card chain-card mb-3">
				<div className="card-body">
					<div className="chain-toolbar">
						<div className="chain-toolbar-group">
							<h5 className="mb-0 me-2">{t('chain_wo_matrix')}</h5>
							<FilterSegment
								options={[{ id: 'amount', label: t('chain_mode_amount') }, { id: 'quantity', label: t('quantity') }]}
								value={cellMode}
								onChange={setCellMode}
							/>
						</div>
						<div className="chain-toolbar-group">
							<HeatLegend mode="danger" />
							<TableSearch value={search} onChange={setSearch} placeholder={t('chain_search_position')} />
						</div>
					</div>

					<div className="chain-scroll">
						<table className="chain-table">
							<thead>
								<tr>
									<th className="chain-index chain-sticky">#</th>
									<SortTh sortKey="product_name" sort={productSort} onSort={toggleProductSort} className="chain-sticky-2">{t('chain_wo_position')}</SortTh>
									{matrixBranches.map(branch => (
										<SortTh key={branch.pos_id} sortKey={`pos:${branch.pos_id}`} sort={productSort} onSort={toggleProductSort} className="text-end">
											<span className="chain-dot" style={{ background: chainColor(branches.indexOf(branch)) }}></span>
											{branch.pos_name}
										</SortTh>
									))}
									<SortTh sortKey={cellMode === 'amount' ? 'amount' : 'quantity'} sort={productSort} onSort={toggleProductSort} className="text-end chain-total-col">{t('total')}</SortTh>
									<SortTh sortKey="share" sort={productSort} onSort={toggleProductSort} className="text-end">{t('share')}</SortTh>
								</tr>
							</thead>
							<tbody>
								{sortedProducts.length === 0 && <ChainEmpty colSpan={matrixBranches.length + 4} />}
								{sortedProducts.map((product, rowIndex) => {
									const rowMax = productRowMax(product)
									return (
										<tr key={product.product_id}>
											<td className="chain-index chain-sticky">{rowIndex + 1}</td>
											<td className="chain-name chain-sticky-2 chain-click" onClick={() => openDetails({ product })} title={t('chain_open_details')}>
												<span>{product.product_name}</span>
												<div className="chain-muted">
													{[product.unit_name, product.category_name, typeLabels[product.product_type_id]].filter(Boolean).join(' · ')}
												</div>
											</td>
											{matrixBranches.map(branch => {
												const value = cellValue(product, branch.pos_id)
												const isMax = value > 0 && value === rowMax && matrixBranches.length > 1
												return (
													<td key={branch.pos_id}
														className={`chain-num ${value > 0 ? 'chain-click' : ''}`}
														style={intensityStyle(value, rowMax, 'danger')}
														title={value > 0 ? `${branch.pos_name}: ${money(product.poses?.[branch.pos_id])} · ${qty(product.quantities?.[branch.pos_id])} ${product.unit_name || ''}` : undefined}
														onClick={() => value > 0 && openDetails({ posId: branch.pos_id, product })}>
														<span style={{ fontWeight: isMax ? 800 : 400 }}>{formatCell(value)}</span>
													</td>
												)
											})}
											<td className="chain-num chain-total-col">
												{cellMode === 'amount' ? money(product.amount) : <>{qty(product.quantity)} <span className="chain-muted">{product.unit_name}</span></>}
											</td>
											<FillCell percent={product.share} tone="danger">{percent(product.share)}</FillCell>
										</tr>
									)
								})}
							</tbody>
							{sortedProducts.length > 0 && cellMode === 'amount' && (
								<tfoot>
									<tr>
										<td className="chain-sticky"></td>
										<td className="chain-sticky-2">{t('chain_wo_total')}</td>
										{matrixBranches.map(branch => (
											<td key={branch.pos_id} className="chain-num chain-click" onClick={() => branch.amount > 0 && openDetails({ posId: branch.pos_id })}>
												{money(branch.amount)}
											</td>
										))}
										<td className="chain-num chain-total-col">{money(totals.amount)}</td>
										<td className="chain-num">100%</td>
									</tr>
								</tfoot>
							)}
						</table>
					</div>
					<div className="chain-muted mt-2">
						<i className="uil uil-info-circle me-1"></i>
						{t('chain_wo_matrix_hint')}
						{topProduct && <> · {t('chain_wo_top_position')}: <b>{topProduct.product_name}</b> ({money(topProduct.amount)}, {percent(topProduct.share)})</>}
					</div>
				</div>
			</div>

			<div className="row g-3 mb-3">
				{data?.notes_count > 0 && (
					<div className="col-xl-5">
						<div className="card chain-card mb-0 h-100">
							<div className="card-body">
								<div className="chain-card-title">
									<h5>{t('chain_wo_reasons')}</h5>
								</div>
								<div className="chain-scroll" style={{ maxHeight: '360px' }}>
									<table className="chain-table">
										<thead>
											<tr>
												<th className="chain-index">#</th>
												<th>{t('note')}</th>
												<th className="text-end">{t('amount')}</th>
												<th className="text-end">{t('chain_wo_documents')}</th>
												<th className="text-end">{t('share')}</th>
											</tr>
										</thead>
										<tbody>
											{reasons.map((reason, index) => (
												<tr key={reason.is_other ? 'other' : reason.key}>
													<td className="chain-index">{index + 1}</td>
													<td className={reason.is_other ? '' : 'chain-click'}
														onClick={reason.is_other ? undefined : () => openDetails({ reason })}
														title={reason.is_other ? undefined : t('chain_open_details')}>
														<span className={reason.key ? '' : 'chain-muted'}>{reasonLabel(reason)}</span>
													</td>
													<td className="chain-num"><b>{money(reason.amount)}</b></td>
													<td className="chain-num">{formatMoney(reason.documents)}</td>
													<FillCell percent={reason.share} tone="danger">{percent(reason.share)}</FillCell>
												</tr>
											))}
										</tbody>
									</table>
								</div>
								<div className="chain-muted mt-2">
									<i className="uil uil-info-circle me-1"></i>
									{t('chain_wo_reasons_hint')}
								</div>
							</div>
						</div>
					</div>
				)}
				<div className={data?.notes_count > 0 ? 'col-xl-7' : 'col-12'}>
					<DailyChart daily={data?.daily} branches={branches} />
				</div>
			</div>

			{/* MODAL DETAILS */}
			<Modal show={!!details} onHide={closeModal} size="xl" centered scrollable>
				<Modal.Header closeButton>
					<Modal.Title>
						{openedDoc
							? <>{t('chain_wo_document')} {documentTitle(openedDoc.document)} — {openedDoc.document.pos_name}</>
							: details?.title}
						<div className="chain-muted fw-normal">
							{openedDoc
								? <>{formatDate(openedDoc.document.created_at, 'dd.MM.yyyy HH:mm')} · {openedDoc.document.wms_name || '—'} · {openedDoc.document.created_by_name || openedDoc.document.created_by || '—'} · {money(openedDoc.document.amount)}</>
								: <>{periodText(applied.start_date, applied.end_date)} · {formatMoney(details?.rows?.length || 0)} {t('chain_wo_documents_lower')} · {money(details?.total)}</>}
						</div>
					</Modal.Title>
				</Modal.Header>
				<Modal.Body style={{ height: '520px' }}>
					{openedDoc ? (
						<>
							<div className="d-flex align-items-center gap-2 mb-2">
								<button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setOpenedDoc(null)}>
									<i className="uil uil-arrow-left me-1"></i>{t('back')}
								</button>
								{openedDoc.document.note && <span className="chain-muted"><b>{t('note')}:</b> {openedDoc.document.note}</span>}
							</div>
							<table className="chain-table">
								<thead>
									<tr>
										<th>#</th>
										<th>{t('chain_wo_position')}</th>
										<th>{t('type')}</th>
										<th>{t('category')}</th>
										<th className="text-end">{t('quantity')}</th>
										<th className="text-end">{t('cost_price')}</th>
										<th className="text-end">{t('amount')}</th>
									</tr>
								</thead>
								<tbody>
									{openedDoc.lines.length === 0 && <ChainEmpty colSpan={7} />}
									{openedDoc.lines.map((line, index) => (
										<tr key={line.id} style={details?.product && details.product.product_id === line.product_id ? { background: 'rgba(240, 101, 72, 0.12)' } : undefined}>
											<td className="chain-index">{index + 1}</td>
											<td>{line.product_name}</td>
											<td>{typeLabels[line.product_type_id] || '—'}</td>
											<td>{line.category_name || '—'}</td>
											<td className="chain-num">{qty(line.quantity)} <span className="chain-muted">{line.unit_name}</span></td>
											<td className="chain-num">{money(line.cost_price)}</td>
											<td className="chain-num"><b>{money(line.total_cost_price)}</b></td>
										</tr>
									))}
								</tbody>
								{openedDoc.lines.length > 0 && (
									<tfoot>
										<tr>
											<td></td>
											<td>{t('total')}</td>
											<td colSpan={4}></td>
											<td className="chain-num">{money(openedDoc.document.amount)}</td>
										</tr>
									</tfoot>
								)}
							</table>
						</>
					) : (
						<>
							{details?.limited && (
								<div className="chain-note mb-2"><i className="uil uil-exclamation-triangle"></i>{t('chain_details_limited')}</div>
							)}
							<table className="chain-table">
								<thead>
									<tr>
										<th>#</th>
										<th>{t('date')}</th>
										<th>{t('number')}</th>
										<th>{t('chain_branch')}</th>
										<th>{t('wms')}</th>
										<th>{t('note')}</th>
										<th>{t('created_by')}</th>
										<th className="text-end">{t('chain_wo_positions')}</th>
										{details?.product && <th className="text-end">{details.product.product_name}</th>}
										<th className="text-end">{t('chain_wo_document_amount')}</th>
									</tr>
								</thead>
								<tbody>
									{(details?.rows || []).length === 0 && <ChainEmpty colSpan={details?.product ? 10 : 9} />}
									{(details?.rows || []).map((row, index) => (
										<tr key={row.id} className="chain-click" onClick={() => openDocument(row.id)} title={t('chain_wo_open_document')}>
											<td className="chain-index">{index + 1}</td>
											<td className="text-nowrap">{formatDate(row.created_at, 'dd.MM.yy HH:mm')}</td>
											<td className="text-nowrap">{documentTitle(row)}</td>
											<td>{row.pos_name}</td>
											<td>{row.wms_name || '—'}</td>
											<td style={{ maxWidth: '240px' }}>{row.note || '—'}</td>
											<td>{row.created_by_name || row.created_by || '—'}</td>
											<td className="chain-num">{formatMoney(row.lines)}</td>
											{details?.product && (
												<td className="chain-num">
													<b>{money(row.product_amount)}</b>
													<div className="chain-muted">{qty(row.product_quantity)} {details.product.unit_name}</div>
												</td>
											)}
											<td className="chain-num"><b>{money(row.amount)}</b></td>
										</tr>
									))}
								</tbody>
								{(details?.rows || []).length > 0 && (
									<tfoot>
										<tr>
											<td></td>
											<td colSpan={7}>{t('total')}</td>
											{details?.product && (
												<td className="chain-num">
													{money(details.product_total)}
													<div className="chain-muted">{qty(details.product_quantity)} {details.product.unit_name}</div>
												</td>
											)}
											<td className="chain-num">{money(details?.total)}</td>
										</tr>
									</tfoot>
								)}
							</table>
						</>
					)}
				</Modal.Body>
				<Modal.Footer>
					<ExcelButton onClick={exportDetails} disabled={openedDoc ? !openedDoc.lines.length : !details?.rows?.length} />
					<button type="button" className="btn btn-outline-secondary" onClick={closeModal}>{t('close')}</button>
				</Modal.Footer>
			</Modal>
		</>
	)
}

// Списания по дням (длинный период — по месяцам), филиалы стопкой
function DailyChart({ daily, branches }) {
	const { t } = useTranslation()
	const prepared = useMemo(() => {
		const source = daily || { days: [], series: [] }
		const active = { ...source, series: source.series.filter(item => item.data.some(value => Number(value) > 0)) }
		return groupSeries(active, source.days.length > 62)
	}, [daily])

	const colorOf = (posId) => chainColor(Math.max(0, branches.findIndex(branch => branch.pos_id === posId)))
	const series = prepared.series.map(item => ({ name: item.pos_name, data: item.data.map(value => Math.round(value)) }))
	const options = {
		chart: { type: 'bar', stacked: true, toolbar: { show: false } },
		plotOptions: { bar: { columnWidth: '70%', borderRadius: 2 } },
		colors: prepared.series.map(item => colorOf(item.pos_id)),
		dataLabels: { enabled: false },
		legend: { position: 'bottom', fontSize: '12px' },
		grid: { borderColor: '#eef2f6', strokeDashArray: 3 },
		xaxis: {
			categories: prepared.labels,
			tickAmount: Math.min(prepared.labels.length, 15),
			labels: { rotate: 0, hideOverlappingLabels: true },
		},
		yaxis: { labels: { formatter: value => formatMoney(Math.round(value / 1000)) + 'k' } },
		tooltip: { shared: true, intersect: false, y: { formatter: value => formatMoney(Math.round(value)) } },
	}

	return (
		<div className="card chain-card mb-0 h-100">
			<div className="card-body">
				<div className="chain-card-title">
					<h5>{(daily?.days || []).length > 62 ? t('chain_by_months') : t('chain_by_days')}</h5>
				</div>
				{series.length > 0
					? <ReactApexChart type="bar" height={340} series={series} options={options} />
					: <div className="chain-empty">{t('chain_no_data')}</div>}
			</div>
		</div>
	)
}

export default WriteOffs
